import { useEffect, useState, useCallback } from 'react';
import {
    Container, Typography, Card, CardContent, Stack, Box, Grid, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    CircularProgress, Divider, LinearProgress,
} from '@mui/material';
import { Icon } from '@iconify/react';
import refreshFill from '@iconify/icons-eva/refresh-fill';
import peopleFill from '@iconify/icons-eva/people-fill';
import briefcaseFill from '@iconify/icons-eva/briefcase-fill';
import trendingUpFill from '@iconify/icons-eva/trending-up-fill';
import activityFill from '@iconify/icons-eva/activity-fill';
import Page from 'src/components/Page';
import { adminSyndicationApi } from 'src/utils/syndicationApi';

const STATUS_COLORS = {
    draft: 'default',
    active: 'info',
    presentation: 'primary',
    qa_phase: 'warning',
    commitment_phase: 'secondary',
    allocating: 'info',
    closed: 'success',
    cancelled: 'error',
};

const formatCurrency = (value) => {
    if (!value && value !== 0) return '—';
    return new Intl.NumberFormat('en-EU', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
};

const formatNumber = (value) => {
    if (!value && value !== 0) return '0';
    return new Intl.NumberFormat('en-US').format(value);
};

const StatCard = ({ icon, iconColor, title, value, subtitle }) => (
    <Card sx={{ height: '100%' }}>
        <CardContent>
            <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                    sx={{
                        width: 48, height: 48, borderRadius: '12px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: `${iconColor}.lighter`, color: `${iconColor}.main`,
                    }}
                >
                    <Icon icon={icon} width={28} height={28} />
                </Box>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>{value}</Typography>
                    <Typography variant="body2" color="text.secondary">{title}</Typography>
                    {subtitle && (
                        <Typography variant="caption" color="text.disabled">{subtitle}</Typography>
                    )}
                </Box>
            </Stack>
        </CardContent>
    </Card>
);

const FunnelBar = ({ label, value, total }) => {
    const pct = total > 0 ? Math.round((value / total) * 100) : 0;
    return (
        <Box sx={{ mb: 1.5 }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="body2">{label}</Typography>
                <Typography variant="body2" fontWeight={600}>{formatNumber(value)} ({pct}%)</Typography>
            </Stack>
            <LinearProgress
                variant="determinate"
                value={pct}
                sx={{ height: 8, borderRadius: 4, bgcolor: 'grey.200' }}
            />
        </Box>
    );
};

export default function AdminAnalytics() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchAnalytics = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await adminSyndicationApi.get('admin/analytics/');
            setData(res.data.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load analytics.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAnalytics();
    }, [fetchAnalytics]);

    if (loading) {
        return (
            <Page title="Syndication Analytics">
                <Container maxWidth="xl" sx={{ py: 3 }}>
                    <Box display="flex" justifyContent="center" py={10}>
                        <CircularProgress />
                    </Box>
                </Container>
            </Page>
        );
    }

    if (error) {
        return (
            <Page title="Syndication Analytics">
                <Container maxWidth="xl" sx={{ py: 3 }}>
                    <Typography color="error">{error}</Typography>
                </Container>
            </Page>
        );
    }

    const { investors, deals, commitments, qa, invitations, recent_activity, top_deals, funnel } = data;

    return (
        <Page title="Syndication Analytics">
            <Container maxWidth="xl" sx={{ py: 3 }}>
                {/* Header */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
                    <Typography variant="h4" fontWeight={700}>Syndication Analytics</Typography>
                    <Chip
                        icon={<Icon icon={refreshFill} width={16} />}
                        label="Refresh"
                        onClick={fetchAnalytics}
                        variant="outlined"
                        sx={{ cursor: 'pointer' }}
                    />
                </Stack>

                {/* KPI Cards Row */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard
                            icon={peopleFill}
                            iconColor="primary"
                            title="Total Investors"
                            value={formatNumber(investors.total)}
                            subtitle={`${investors.active} active`}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard
                            icon={briefcaseFill}
                            iconColor="info"
                            title="Total Deals"
                            value={formatNumber(deals.total)}
                            subtitle={`${deals.active_deals} active, ${deals.closed_deals} closed`}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard
                            icon={trendingUpFill}
                            iconColor="success"
                            title="Capital Raised"
                            value={formatCurrency(deals.total_capital_raised)}
                            subtitle={`Avg deal: ${formatCurrency(deals.avg_deal_size)}`}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard
                            icon={activityFill}
                            iconColor="warning"
                            title="Commitments"
                            value={formatNumber(commitments.total)}
                            subtitle={`${formatCurrency(commitments.total_committed_amount)} total`}
                        />
                    </Grid>
                </Grid>

                {/* Row 2: Pipeline + Investor Breakdown */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    {/* Deal Pipeline */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" fontWeight={600} mb={2}>Deal Pipeline</Typography>
                                <Stack spacing={1}>
                                    {deals.by_status && deals.by_status.map((s) => (
                                        <Stack key={s.status} direction="row" justifyContent="space-between" alignItems="center">
                                            <Chip
                                                label={s.status?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                                                color={STATUS_COLORS[s.status] || 'default'}
                                                size="small"
                                                sx={{ minWidth: 120 }}
                                            />
                                            <Typography variant="h6" fontWeight={600}>{s.count}</Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Investor Status Breakdown */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" fontWeight={600} mb={2}>Investor Status</Typography>
                                <Stack spacing={1}>
                                    {[
                                        { label: 'Active', value: investors.active, color: 'success' },
                                        { label: 'Pending Approval', value: investors.pending_approval, color: 'warning' },
                                        { label: 'Pending Email', value: investors.pending_email, color: 'info' },
                                        { label: 'Approved', value: investors.approved, color: 'primary' },
                                        { label: 'Suspended', value: investors.suspended, color: 'error' },
                                        { label: 'Rejected', value: investors.rejected, color: 'default' },
                                    ].map((row) => (
                                        <Stack key={row.label} direction="row" justifyContent="space-between" alignItems="center">
                                            <Chip label={row.label} color={row.color} size="small" sx={{ minWidth: 140 }} />
                                            <Typography variant="h6" fontWeight={600}>{row.value}</Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Row 3: Commitment Breakdown + Conversion Funnel */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    {/* Commitment by Decision Type */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" fontWeight={600} mb={2}>Commitments by Decision</Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Decision</TableCell>
                                                <TableCell align="right">Count</TableCell>
                                                <TableCell align="right">Total Amount</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {commitments.by_decision && commitments.by_decision.map((row) => (
                                                <TableRow key={row.decision}>
                                                    <TableCell>
                                                        <Chip
                                                            label={row.decision?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">{row.count}</TableCell>
                                                    <TableCell align="right">{formatCurrency(row.total_amount)}</TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow>
                                                <TableCell><strong>Withdrawn</strong></TableCell>
                                                <TableCell align="right">{commitments.withdrawn_count}</TableCell>
                                                <TableCell align="right">—</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell><strong>Locked</strong></TableCell>
                                                <TableCell align="right">{commitments.locked_count}</TableCell>
                                                <TableCell align="right">—</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <Divider sx={{ my: 1.5 }} />
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="body2" color="text.secondary">Avg Commitment</Typography>
                                    <Typography variant="body2" fontWeight={600}>{formatCurrency(commitments.avg_commitment_amount)}</Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="body2" color="text.secondary">Total Allocated</Typography>
                                    <Typography variant="body2" fontWeight={600}>{formatCurrency(commitments.total_allocated_amount)}</Typography>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Conversion Funnel */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" fontWeight={600} mb={2}>Investor Conversion Funnel</Typography>
                                <FunnelBar label="Registered" value={funnel.total_investors} total={funnel.total_investors} />
                                <FunnelBar label="Email Verified" value={funnel.verified_email} total={funnel.total_investors} />
                                <FunnelBar label="Approved" value={funnel.approved} total={funnel.total_investors} />
                                <FunnelBar label="Active (2FA Set Up)" value={funnel.active} total={funnel.total_investors} />
                                <FunnelBar label="Has Commitments" value={funnel.with_commitments} total={funnel.total_investors} />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Row 4: Q&A + Invitations */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="subtitle2" color="text.secondary" mb={1}>Q&A Activity</Typography>
                                <Typography variant="h3" fontWeight={700}>{qa.total_questions}</Typography>
                                <Typography variant="body2" color="text.secondary">questions total</Typography>
                                <Divider sx={{ my: 1 }} />
                                <Stack spacing={0.5}>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="caption">Approved</Typography>
                                        <Typography variant="caption" fontWeight={600}>{qa.approved}</Typography>
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="caption">Pending</Typography>
                                        <Typography variant="caption" fontWeight={600}>{qa.pending_moderation}</Typography>
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="caption">Rejected</Typography>
                                        <Typography variant="caption" fontWeight={600}>{qa.rejected}</Typography>
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="caption">Answers</Typography>
                                        <Typography variant="caption" fontWeight={600}>{qa.total_answers}</Typography>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="subtitle2" color="text.secondary" mb={1}>Invitations</Typography>
                                <Typography variant="h3" fontWeight={700}>{invitations.total_sent}</Typography>
                                <Typography variant="body2" color="text.secondary">invitations sent</Typography>
                                <Divider sx={{ my: 1 }} />
                                <Stack spacing={0.5}>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="caption">Accessed</Typography>
                                        <Typography variant="caption" fontWeight={600}>{invitations.total_accessed}</Typography>
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="caption">Access Rate</Typography>
                                        <Typography variant="caption" fontWeight={600}>{invitations.access_rate}%</Typography>
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="caption">Revoked</Typography>
                                        <Typography variant="caption" fontWeight={600}>{invitations.revoked}</Typography>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* 30-Day Snapshot */}
                    <Grid item xs={12} sm={12} md={6}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="subtitle2" color="text.secondary" mb={1}>Last 30 Days</Typography>
                                <Grid container spacing={2}>
                                    {[
                                        { label: 'New Investors', value: investors.registrations_last_30d },
                                        { label: 'New Deals', value: deals.deals_last_30d },
                                        { label: 'Commitments', value: commitments.commitments_last_30d },
                                        { label: 'Questions', value: qa.questions_last_30d },
                                        { label: 'Invitations', value: invitations.invitations_last_30d },
                                    ].map((item) => (
                                        <Grid item xs={6} key={item.label}>
                                            <Typography variant="h5" fontWeight={700}>{formatNumber(item.value)}</Typography>
                                            <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Row 5: Top Deals + Recent Activity */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    {/* Top Deals */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" fontWeight={600} mb={2}>Top Deals by Commitment Volume</Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Deal</TableCell>
                                                <TableCell align="right">Status</TableCell>
                                                <TableCell align="right">Commitments</TableCell>
                                                <TableCell align="right">Total Committed</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {top_deals && top_deals.map((deal) => (
                                                <TableRow key={deal.id}>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={600}>{deal.title}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{deal.deal_code}</Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Chip
                                                            label={deal.status?.replace(/_/g, ' ')}
                                                            color={STATUS_COLORS[deal.status] || 'default'}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">{deal.commitment_count || 0}</TableCell>
                                                    <TableCell align="right">{formatCurrency(deal.total_committed)}</TableCell>
                                                </TableRow>
                                            ))}
                                            {(!top_deals || top_deals.length === 0) && (
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center">
                                                        <Typography variant="body2" color="text.secondary">No deals with commitments yet.</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Recent Activity */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" fontWeight={600} mb={2}>Recent Activity</Typography>
                                <Stack spacing={1} sx={{ maxHeight: 400, overflowY: 'auto' }}>
                                    {recent_activity && recent_activity.map((act) => (
                                        <Box key={act.id} sx={{ p: 1, borderRadius: 1, bgcolor: 'grey.50' }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {act.action?.replace(/_/g, ' ')}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {act.deal__title || 'N/A'} &middot; {act.actor_role}
                                                        {act.actor__first_name && ` · ${act.actor__first_name} ${act.actor__last_name || ''}`}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="caption" color="text.disabled" sx={{ whiteSpace: 'nowrap' }}>
                                                    {act.created ? new Date(act.created).toLocaleString() : ''}
                                                </Typography>
                                            </Stack>
                                            {act.description && (
                                                <Typography variant="caption" color="text.secondary">{act.description}</Typography>
                                            )}
                                        </Box>
                                    ))}
                                    {(!recent_activity || recent_activity.length === 0) && (
                                        <Typography variant="body2" color="text.secondary" textAlign="center">
                                            No recent activity.
                                        </Typography>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Investor Distribution */}
                <Grid container spacing={2}>
                    {/* By Type */}
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" fontWeight={600} mb={2}>Investors by Type</Typography>
                                {investors.by_type && investors.by_type.length > 0 ? (
                                    <Stack spacing={1}>
                                        {investors.by_type.map((t) => (
                                            <Stack key={t.investor_type} direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography variant="body2">
                                                    {(t.investor_type || 'Not Specified').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                                                </Typography>
                                                <Chip label={t.count} size="small" variant="outlined" />
                                            </Stack>
                                        ))}
                                    </Stack>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">No active investors yet.</Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* By Country */}
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" fontWeight={600} mb={2}>Top 10 Countries</Typography>
                                {investors.by_country && investors.by_country.length > 0 ? (
                                    <Stack spacing={1}>
                                        {investors.by_country.map((c, idx) => (
                                            <Stack key={idx} direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography variant="body2">{c.country__name || 'Unknown'}</Typography>
                                                <Chip label={c.count} size="small" variant="outlined" />
                                            </Stack>
                                        ))}
                                    </Stack>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">No country data available.</Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Container>
        </Page>
    );
}
